import React, { useEffect, useState, useRef } from "react";
import { GridLoader } from "react-spinners";
import { useGesture } from "react-use-gesture";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import cdn from "../utils/cdn";


const breakpoints = [755]
const mq = breakpoints.map(bp => `@media (min-width: ${bp}px)`)

const ProductImages = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef(null);
  const isAutoScrolling = useRef(false);
  const [viewImageIndex, setViewImageIndex] = useState(null);
  const [pinchZoom, setPinchZoom] = useState(1);
  const isPinching = useRef(false);
  const initialDistance = useRef(null);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    let scrollTimeout;
    const handleScroll = () => {
      // Clear the previous timeout so we only act after scrolling has stopped.
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (containerRef.current && containerRef.current.firstChild) {
          const cardWidth = containerRef.current.firstChild.offsetWidth;
          const newIndex = Math.round(containerRef.current.scrollLeft / cardWidth);

          setCurrentIndex(newIndex);
        }
      }, 20); // Adjust the delay (in ms) as needed.
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const carouselSwipe = useGesture(
    {
      onDragEnd: ({ movement: [mx], memo = containerRef.current ? containerRef.current.scrollLeft : 0, last }) => {
        if (containerRef.current) {
          containerRef.current.scrollLeft = memo - mx;
        }
        if (last && containerRef.current) {
          const cardWidth = containerRef.current.firstChild.offsetWidth;
          const newIndex = Math.round(containerRef.current.scrollLeft / cardWidth);

          setCurrentIndex(newIndex);
          containerRef.current.scrollTo({
            left: newIndex * cardWidth,
            behavior: "smooth",
          });
        }
        return memo;
      },
    },
    { drag: { axis: "x", swipe: 10 } }
  );

  const swipeHandlers = useGesture({
    onSwipe: ({ direction: [xDir] }) => {
      alert(1);
      if (viewImageIndex !== null) {
        if (xDir === -1) {
          setViewImageIndex((prevIndex) => {
            const newIndex = prevIndex === images.length - 1 ? 0 : prevIndex + 1;
            setCurrentIndex(newIndex);
            return newIndex;
          });
        } else if (xDir === 1) {
          setViewImageIndex((prevIndex) => {
            const newIndex = prevIndex === 0 ? images.length - 1 : prevIndex - 1;
            setCurrentIndex(newIndex);
            return newIndex;
          });
        }
      }
    },
  });

  const initialPinchCenter = useRef({ x: 0, y: 0 });

  const bindPinch = useGesture(
    {
      onPinchStart: ({ event }) => {
        event.stopPropagation();
        const touches = event.touches;
        if (touches && touches.length >= 2) {
          const dx = touches[0].clientX - touches[1].clientX;
          const dy = touches[0].clientY - touches[1].clientY;
          initialDistance.current = Math.hypot(dx, dy);
          // Compute and store the initial center of the pinch
          initialPinchCenter.current = {
            x: (touches[0].clientX + touches[1].clientX) / 2,
            y: (touches[0].clientY + touches[1].clientY) / 2,
          };
          isPinching.current = true;
        }
      },
      onPinch: ({ event }) => {
        event.stopPropagation();
        const touches = event.touches;
        if (touches && touches.length >= 2 && initialDistance.current) {
          const dx = touches[0].clientX - touches[1].clientX;
          const dy = touches[0].clientY - touches[1].clientY;
          const currentDistance = Math.hypot(dx, dy);
          const scale = currentDistance / initialDistance.current;
          setPinchZoom(scale);
          // Compute the current center and delta from the initial center
          const currentCenter = {
            x: (touches[0].clientX + touches[1].clientX) / 2,
            y: (touches[0].clientY + touches[1].clientY) / 2,
          };
          const deltaX = currentCenter.x - initialPinchCenter.current.x;
          const deltaY = currentCenter.y - initialPinchCenter.current.y;
          setImageOffset({ x: deltaX, y: deltaY });
        }
      },
      onPinchEnd: ({ event }) => {
        event.stopPropagation();
        isPinching.current = false;
        setPinchZoom(1); // Reset zoom
        initialDistance.current = null;
        setImageOffset({ x: 0, y: 0 });
      },
    },
    { pinch: { scaleBounds: { min: 1, max: 3 } } }
  );

  useEffect(() => {
    if (viewImageIndex !== null) {
      const scrollY = window.scrollY;
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflow = "hidden";
      document.body.style.width = "100%";

      return () => {
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.overflow = "";
        document.body.style.width = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [viewImageIndex]);

  useEffect(() => {
    const cardWidth = containerRef.current?.firstChild?.offsetWidth || 0;
    if (containerRef.current) {
      containerRef.current.scrollTo({
        left: currentIndex * cardWidth,
        behavior: isAutoScrolling.current ? "smooth" : "instant",
      });

      const timer = setTimeout(() => {
        isAutoScrolling.current = false;
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [currentIndex, images]);

  const handleDotClick = (index) => {
    setCurrentIndex(index);
  };

  if (!images || images.length === 0) {
    return (
      <div css={{}}>
        <div ref={containerRef} css={{ ...styles.carouselContainer, width: "100%", padding: "0px 10px", justifyContent: "center" }}>
          <div
            css={{
              ...styles.card,
              width: "100%",
            }}
          >
            <img src="https://placehold.co/800x450?text=No+Image+Available" alt="Not Available" css={styles.image} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div css={{}}>
      <div
        ref={containerRef}
        {...carouselSwipe()}
        css={{ ...styles.carouselContainer, padding: images?.length === 1 ? "0px 10px" : "0 10px", touchAction: "" }}
      >
        {images?.map((image, index) => (
          <div
            key={index}
            onClick={() => {
              if (images?.banner_target_url?.startsWith("http://") || images?.banner_target_url?.startsWith("https://")) {
                window.location.href = images.banner_target_url;
              } else {
                setViewImageIndex(index);
              }
            }}
            css={{
              ...styles.card,
              flex: images?.length === 1 ? "0 0 100%" : "0 0 100%",
            }}
          >
            <img src={image || "https://placehold.co/800x450"} alt={images.title} css={{ ...styles.image }} />
            <div css={styles.loaderOverlay}>
              <GridLoader />
            </div>
          </div>
        ))}
      </div>

      {images?.length > 1 && (
        <div css={styles.dotsContainer}>
          {images?.map((_, index) => {
            const isActive = currentIndex % images?.length === index;

            return (
              <div
                key={index}
                onClick={() => handleDotClick(index)}
                css={{
                  ...styles.dot,
                  backgroundColor: isActive ? "black" : "#b2b2b2",
                  opacity: isActive ? 1 : 0.5,
                }}
              ></div>
            );
          })}
        </div>
      )}

      {viewImageIndex !== null && (
        <div
          css={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
            overflow: "hidden",
            touchAction: "none",
          }}
          {...swipeHandlers()}
        >
          <div
            css={{
              position: "absolute",
              top: 0,
              right: 0,
              padding: 15,
              cursor: "pointer",
              color: "white",
              fontSize: 34,
            }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent event propagation
              setViewImageIndex(null);
            }}
          >
            &times;
          </div>
          <img
            aspectRatio="4/3"
            width={560}
            src={cdn.image(images[viewImageIndex])}
            alt="Full Screen"
            css={{
              maxWidth: "90%",
              maxHeight: "90%",
              transform: `translate(${imageOffset.x}px, ${imageOffset.y}px) scale(${pinchZoom})`,
              touchAction: "none",
            }}
            {...bindPinch()}
          />
          <div
            css={{ position: "absolute", padding: 10, left: "15px", marginTop: -25, top: "50%" }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent event propagation
              setViewImageIndex((prevIndex) => (prevIndex === 0 ? images?.length - 1 : prevIndex - 1));
            }}
          >
            <ArrowBackIos />
          </div>
          <div
            css={{ position: "absolute", padding: 10, right: "15px", marginTop: -25, top: "50%" }}
            onClick={(e) => {
              e.stopPropagation(); // Prevent event propagation
              setViewImageIndex((prevIndex) => (prevIndex === images?.length - 1 ? 0 : prevIndex + 1));
            }}
          >
            <ArrowForwardIos />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductImages;

const styles = {
  carouselContainer: {
    display: "flex",
    overflowX: "auto",
    scrollBehavior: "smooth",
    scrollSnapType: "x mandatory",
    gap: "15px",
    maxWidth: "455px",
    scrollbarWidth: "none",
    [mq[0]]: {
      maxWidth: "575px",
    },
  },
  card: {
    scrollSnapAlign: "center",
    position: "relative",
    overflow: "hidden",
    borderRadius: "12px",
    maxHeight: "440px",
    maxWidth: "440px",
    border: "1px solid #e0e0e0",
    background: "#fafafa",
    zIndex: 1,
    [mq[0]]: {
      maxHeight: "560px",
      maxWidth: "560px",
    },
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    aspectRatio: "4/3",
    zIndex: 2,
    position: "relative",
    background: "white",
  },
  loaderOverlay: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
    color: "white",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    zIndex: -2,
    opacity: 0.4,
  },
  dotsContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "5px",
    height: 40,
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    cursor: "pointer",
  },
};
